import React from "react";
import ReactDOM from "react-dom/client";
import {
  buildContentLibraryPickerUrl,
  ensureAuthorizedDeduped,
  parseEmbeddedPickerMessage,
  setSessionIdCookie,
  DEFAULT_SESSION_COOKIE_MINUTES,
  DEFAULT_SESSION_ID_COOKIE_NAME,
} from "@clickd/content-library-embedded-picker-core";
import ContentLibraryPickerOverlay from "./ContentLibraryPickerOverlay";

export interface ContentLibraryPickerResult {
  url: string;
}

interface OpenContentLibraryPickerParams {
  accountKey: string;
  sessionId?: string | null;
  userId?: string | null;
}

function OpenContentLibraryPicker({
  accountKey,
  sessionId,
  userId,
}: OpenContentLibraryPickerParams): Promise<ContentLibraryPickerResult | null> {
  if (!accountKey) {
    return Promise.reject(new Error("accountKey is required to open Content Library"));
  }

  const hostname = (window.location && window.location.hostname) || "";
  const region = process.env.REACT_APP_REGION;

  const sessionIdSafe = sessionId || null;
  if (sessionIdSafe) {
    const isClickdimensions = hostname.toLowerCase().endsWith(".clickdimensions.com");
    const isHttps = window.location && window.location.protocol === "https:";

    setSessionIdCookie({
      sessionId: sessionIdSafe,
      expirationMinutes: DEFAULT_SESSION_COOKIE_MINUTES,
      cookieName: DEFAULT_SESSION_ID_COOKIE_NAME,
      prefix: userId || "formeditor",
      domain: isClickdimensions ? ".clickdimensions.com" : undefined,
      sameSiteNoneSecure: isClickdimensions && isHttps,
    });
  }

  const authorizePromise = sessionIdSafe
    ? ensureAuthorizedDeduped({
        mscrmappBaseUrl: process.env.REACT_APP_WEBSERVICE_URL,
        region,
        hostname,
        accountKey,
        sessionId: sessionIdSafe,
      }).catch(() => {
        // ignore (best-effort)
      })
    : Promise.resolve();

  let url = buildContentLibraryPickerUrl({
    region,
    hostname,
    accountKey,
  });
  url += `${url.includes("?") ? "&" : "?"}fileType=image`;

  return authorizePromise.then(
    () =>
      new Promise((resolve) => {
        const portal = document.getElementById("portal") || document.body;
        const container = document.createElement("div");
        portal.appendChild(container);

        const root = ReactDOM.createRoot(container);
        let hasResolved = false;

        const messageAbortController = new AbortController();

        function cleanup() {
          try {
            messageAbortController.abort();
          } catch {
            // ignore
          }

          try {
            root.unmount();
          } catch {
            // ignore
          }

          try {
            if (container.parentElement) {
              container.parentElement.removeChild(container);
            }
          } catch {
            // ignore
          }
        }

        function resolveAndCleanup(result: ContentLibraryPickerResult | null) {
          if (hasResolved) return;
          hasResolved = true;
          cleanup();
          resolve(result);
        }

        function onMessage(event: MessageEvent) {
          const parsed = parseEmbeddedPickerMessage(event, {
            iframeUrl: url,
            expectedFileTypes: ["image"],
          });
          if (parsed.kind === "close") {
            resolveAndCleanup(null);
            return;
          }

          if (parsed.kind === "asset" && parsed.asset && parsed.asset.url) {
            resolveAndCleanup({ url: parsed.asset.url });
          }
        }

        window.addEventListener("message", onMessage, { signal: messageAbortController.signal });

        root.render(
          <ContentLibraryPickerOverlay url={url} onCancel={() => resolveAndCleanup(null)} />,
        );
      }),
  );
}

export default OpenContentLibraryPicker;
